import React from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  FormControl,
  Select,
  InputLabel,
  Tooltip,
  Skeleton
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as TenantsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  People as UsersIcon,
  Apartment as AccountsIcon,
  Contacts as ContactsIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  Article as ArticleIcon,
  Folder as FolderIcon,
  Security as SecurityIcon,
  Language as DomainIcon,
  Badge as RolesIcon,
  Groups as GroupsIcon,
  Apps as AppsIcon,
  Hub as ServicesIcon,
  AttachMoney as PricingIcon,
  Tune as MetricsIcon,
  HowToReg as SignupIcon,
  Palette as PaletteIcon,
  ViewModule as AcrImagesIcon,
  CloudQueue as InfrastructureIcon,
  Policy as IpAccessIcon,
  AccountTree as RepositoryIcon,
  SupportAgent as HelpDeskIcon,
  ConfirmationNumber as TicketsIcon,
  Description as ReleaseNotesIcon
} from '@mui/icons-material';
import { useAuth, usePermissions } from '../context/AuthContext';
import { useThemeTemplate } from '../context/ThemeTemplateContext';
import { BRAND } from '../constants/branding';
import TenantHubLogo from './TenantHubLogo';
import AppVersionDetailsDialog from './AppVersionDetailsDialog';
import api from '../utils/api';

const drawerWidth = 260;

const VERSION_BOX_STYLES = {
  Frontend: {
    bg: 'linear-gradient(145deg, #e0f7fa 0%, #b2ebf2 100%)',
    border: '#4dd0e1',
    label: '#006064',
    value: '#00838f'
  },
  Backend: {
    bg: 'linear-gradient(145deg, #fff8e1 0%, #ffe082 100%)',
    border: '#ffca28',
    label: '#e65100',
    value: '#f57c00'
  },
  Database: {
    bg: 'linear-gradient(145deg, #e8f5e9 0%, #a5d6a7 100%)',
    border: '#66bb6a',
    label: '#1b5e20',
    value: '#2e7d32'
  },
  All: {
    bg: 'linear-gradient(145deg, #e3f2fd 0%, #90caf9 100%)',
    border: '#42a5f5',
    label: '#0d47a1',
    value: '#1565c0'
  }
};

const VersionBox = ({ label, version, loading, onClick, subtitle }) => {
  const colors = VERSION_BOX_STYLES[label] || VERSION_BOX_STYLES.Frontend;
  return (
    <Tooltip
      title={version ? `Click for ${label} image details` : `TenantHub ${label} version unavailable`}
      arrow
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.(e);
          }
        }}
        sx={{
          px: 1.35,
          py: 0.55,
          minWidth: label === 'All' ? 72 : 108,
          borderRadius: 1.5,
          border: `1.5px solid ${colors.border}`,
          background: colors.bg,
          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
          textAlign: 'center',
          lineHeight: 1.15,
          cursor: 'pointer',
          transition: 'transform 0.12s ease, box-shadow 0.12s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 3px 10px rgba(0,0,0,0.18)'
          },
          '&:focus-visible': {
            outline: `2px solid ${colors.value}`,
            outlineOffset: 2
          }
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: colors.label,
            fontWeight: 800,
            fontSize: '0.62rem',
            letterSpacing: 0.55,
            textTransform: 'uppercase'
          }}
        >
          {label}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={96} height={16} sx={{ bgcolor: 'rgba(0,0,0,0.12)', mx: 'auto' }} />
        ) : (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: colors.value,
              fontWeight: 800,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '0.68rem',
              maxWidth: label === 'All' ? 88 : 168,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mx: 'auto'
            }}
          >
            {subtitle || version || '—'}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const { template, templateId, templates, setTemplateId } = useThemeTemplate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [settingsOpen, setSettingsOpen] = React.useState(location.pathname.startsWith('/settings'));
  const [helpDeskOpen, setHelpDeskOpen] = React.useState(location.pathname.startsWith('/help-desk'));
  const [appVersions, setAppVersions] = React.useState(null);
  const [versionsLoading, setVersionsLoading] = React.useState(true);
  const [versionDialogOpen, setVersionDialogOpen] = React.useState(false);
  const [versionDialogComponent, setVersionDialogComponent] = React.useState('Frontend');
  const [versionDialogShowAll, setVersionDialogShowAll] = React.useState(false);

  React.useEffect(() => {
    if (location.pathname.startsWith('/settings')) {
      setSettingsOpen(true);
    }
    if (location.pathname.startsWith('/help-desk')) {
      setHelpDeskOpen(true);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setVersionsLoading(true);
      try {
        const res = await api.get('/settings/app-versions');
        if (!cancelled) setAppVersions(res.data?.data || null);
      } catch {
        if (!cancelled) setAppVersions(null);
      } finally {
        if (!cancelled) setVersionsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const mainMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', permission: null },
    { text: 'Tenants', icon: <TenantsIcon />, path: '/tenants', permission: ['tenants', 'view'] },
    { text: 'Signup Requests', icon: <SignupIcon />, path: '/signup-requests', permission: ['tenants', 'view'] },
    { text: 'Accounts', icon: <AccountsIcon />, path: '/accounts', permission: ['accounts', 'view'] },
    { text: 'Contacts', icon: <ContactsIcon />, path: '/contacts', permission: ['accounts', 'view'] },
    { text: 'Users', icon: <UsersIcon />, path: '/users', permission: ['users', 'view'] }
  ].filter((item) => !item.permission || can(item.permission[0], item.permission[1]));

  const helpDeskMenuItems = [
    { text: 'All Tickets', icon: <TicketsIcon />, path: '/help-desk/tickets', permission: ['helpdesk', 'view'] }
  ].filter((item) => !item.permission || can(item.permission[0], item.permission[1]));

  const settingsMenuItems = [
    { text: 'General Information', icon: <ArticleIcon />, path: '/settings/general', permission: ['settings', 'view'] },
    { text: 'System Information', icon: <InfoIcon />, path: '/settings/system-information', permission: ['settings', 'view'] },
    { text: 'File Location', icon: <FolderIcon />, path: '/settings/file-location', permission: ['settings', 'view'] },
    { text: 'Repository Settings', icon: <RepositoryIcon />, path: '/settings/repositories', permission: ['settings', 'view'] },
    { text: 'Domain Setup', icon: <DomainIcon />, path: '/settings/domains', permission: ['settings', 'view'] },
    { text: 'Software Applications', icon: <AppsIcon />, path: '/settings/software-applications', permission: ['settings', 'view'] },
    { text: 'Container Images (ACR)', icon: <AcrImagesIcon />, path: '/settings/acr-images', permission: ['settings', 'view'] },
    { text: 'Infrastructure', icon: <InfrastructureIcon />, path: '/settings/infrastructure', permission: ['settings', 'view'] },
    { text: 'IP Allow/Deny List', icon: <IpAccessIcon />, path: '/settings/ip-access', permission: ['settings', 'view'] },
    { text: 'Services / Framework', icon: <ServicesIcon />, path: '/settings/services', permission: ['settings', 'view'] },
    { text: 'Pricing Metrics', icon: <MetricsIcon />, path: '/settings/pricing-metrics', permission: ['settings', 'view'] },
    { text: 'Pricing', icon: <PricingIcon />, path: '/settings/pricing', permission: ['settings', 'view'] },
    { text: 'Roles', icon: <RolesIcon />, path: '/settings/roles', permission: ['roles', 'view'] },
    { text: 'Groups', icon: <GroupsIcon />, path: '/settings/groups', permission: ['groups', 'view'] },
    { text: 'Multi-factor Authentication', icon: <SecurityIcon />, path: '/settings/mfa', permission: ['settings', 'view'] },
    { text: 'Release Notes', icon: <ReleaseNotesIcon />, path: '/settings/release-notes', permission: ['settings', 'view'] }
  ].filter((item) => !item.permission || can(item.permission[0], item.permission[1]));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: template.appBar
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flexShrink: 0 }}>
            <TenantHubLogo size={40} gradient={template.logoGradient} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap sx={{ fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                {BRAND.productName}
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.72)', display: 'block' }}>
                {BRAND.tagline}
              </Typography>
            </Box>
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 1,
                ml: 1.5,
                pl: 1.5,
                borderLeft: '1px solid rgba(255,255,255,0.28)'
              }}
            >
              <VersionBox
                label="Frontend"
                version={appVersions?.frontend?.version}
                loading={versionsLoading}
                onClick={() => {
                  setVersionDialogComponent('Frontend');
                  setVersionDialogShowAll(false);
                  setVersionDialogOpen(true);
                }}
              />
              <VersionBox
                label="Backend"
                version={appVersions?.backend?.version}
                loading={versionsLoading}
                onClick={() => {
                  setVersionDialogComponent('Backend');
                  setVersionDialogShowAll(false);
                  setVersionDialogOpen(true);
                }}
              />
              <VersionBox
                label="Database"
                version={appVersions?.database?.version}
                loading={versionsLoading}
                onClick={() => {
                  setVersionDialogComponent('Database');
                  setVersionDialogShowAll(false);
                  setVersionDialogOpen(true);
                }}
              />
              <VersionBox
                label="All"
                subtitle="services"
                loading={versionsLoading}
                onClick={() => {
                  setVersionDialogComponent('Frontend');
                  setVersionDialogShowAll(true);
                  setVersionDialogOpen(true);
                }}
              />
            </Box>
          </Box>

          <Box sx={{ flex: 1 }} />

          <FormControl size="small" sx={{ minWidth: 170, display: { xs: 'none', lg: 'flex' }, flexShrink: 0 }}>
            <InputLabel id="template-chooser-label" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Template
            </InputLabel>
            <Select
              labelId="template-chooser-label"
              label="Template"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              startAdornment={<PaletteIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 0.5, fontSize: 18 }} />}
              sx={{
                color: '#fff',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.45)' },
                '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' }
              }}
            >
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* User Menu */}
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0 }}>
            <Avatar
              sx={{
                bgcolor: template.accent,
                fontWeight: 600
              }}
            >
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">{user?.first_name} {user?.last_name}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">{user?.email}</Typography>
              <Typography variant="caption" color="primary" sx={{ textTransform: 'capitalize' }}>
                {user?.role?.replace('_', ' ')}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile'); }}>
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: template.drawer,
            borderRight: '1px solid #1e293b'
          }
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', mt: 2 }}>
          <List>
            {mainMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ px: 1 }}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  selected={location.pathname === item.path}
                  sx={{
                    borderRadius: 2,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(245, 158, 11, 0.15)',
                      '&:hover': {
                        bgcolor: 'rgba(245, 158, 11, 0.25)'
                      },
                      '& .MuiListItemIcon-root': {
                        color: '#f59e0b'
                      },
                      '& .MuiListItemText-primary': {
                        color: '#f59e0b',
                        fontWeight: 600
                      }
                    },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: '#94a3b8', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{ '& .MuiListItemText-primary': { color: '#e2e8f0' } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}

            {helpDeskMenuItems.length > 0 && (
            <ListItem disablePadding sx={{ px: 1 }}>
              <ListItemButton
                onClick={() => setHelpDeskOpen((prev) => !prev)}
                selected={location.pathname.startsWith('/help-desk')}
                sx={{
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(245, 158, 11, 0.15)',
                    '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.25)' }
                  },
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                }}
              >
                <ListItemIcon sx={{ color: '#94a3b8', minWidth: 40 }}>
                  <HelpDeskIcon />
                </ListItemIcon>
                <ListItemText primary="Help Desk" sx={{ '& .MuiListItemText-primary': { color: '#e2e8f0' } }} />
                {helpDeskOpen ? <ExpandLess sx={{ color: '#94a3b8' }} /> : <ExpandMore sx={{ color: '#94a3b8' }} />}
              </ListItemButton>
            </ListItem>
            )}

            <Collapse in={helpDeskOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {helpDeskMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding sx={{ pl: 3, pr: 1 }}>
                    <ListItemButton
                      onClick={() => navigate(item.path)}
                      selected={location.pathname === item.path}
                      sx={{
                        borderRadius: 2,
                        '&.Mui-selected': {
                          bgcolor: 'rgba(245, 158, 11, 0.15)',
                          '& .MuiListItemIcon-root': { color: '#f59e0b' },
                          '& .MuiListItemText-primary': { color: '#f59e0b', fontWeight: 600 }
                        },
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#94a3b8', minWidth: 36 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} sx={{ '& .MuiListItemText-primary': { color: '#e2e8f0' } }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>

            {settingsMenuItems.length > 0 && (
            <ListItem disablePadding sx={{ px: 1 }}>
              <ListItemButton
                onClick={() => setSettingsOpen((prev) => !prev)}
                selected={location.pathname.startsWith('/settings')}
                sx={{
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(245, 158, 11, 0.15)',
                    '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.25)' }
                  },
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                }}
              >
                <ListItemIcon sx={{ color: '#94a3b8', minWidth: 40 }}>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText primary="Settings" sx={{ '& .MuiListItemText-primary': { color: '#e2e8f0' } }} />
                {settingsOpen ? <ExpandLess sx={{ color: '#94a3b8' }} /> : <ExpandMore sx={{ color: '#94a3b8' }} />}
              </ListItemButton>
            </ListItem>
            )}

            <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {settingsMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding sx={{ pl: 3, pr: 1 }}>
                    <ListItemButton
                      onClick={() => navigate(item.path)}
                      selected={location.pathname === item.path}
                      sx={{
                        borderRadius: 2,
                        '&.Mui-selected': {
                          bgcolor: 'rgba(245, 158, 11, 0.15)',
                          '& .MuiListItemIcon-root': { color: '#f59e0b' },
                          '& .MuiListItemText-primary': { color: '#f59e0b', fontWeight: 600 }
                        },
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#94a3b8', minWidth: 36 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} sx={{ '& .MuiListItemText-primary': { color: '#e2e8f0' } }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          bgcolor: '#f1f5f9',
          minHeight: '100vh'
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      <AppVersionDetailsDialog
        open={versionDialogOpen}
        onClose={() => setVersionDialogOpen(false)}
        initialComponent={versionDialogComponent}
        initialShowAll={versionDialogShowAll}
        summaryVersions={appVersions}
      />
    </Box>
  );
};

export default Layout;
